import { FileIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns/fp';
import { useState } from 'react';
import { Link, useLoaderData, useLocation } from 'react-router';

import { OptionalDetailsSection } from '~/components/content/optional-details-section';
import { Button } from '~/components/ui/button';
import { Icon } from '~/components/ui/icon';
import { getAthleteGymDetailsById } from '~/domain/gyms/queries/get-athlete-gym-details-by-id.server';
import { parseTime } from '~/domain/shared/time-helpers';
import { getSessionIfAuthenticated } from '~/utils/auth.server';
import { handleDomainError } from '~/utils/errors.server';
import { getGymIdAndSlugFromParams } from '~/utils/gyms.server';

import { AuthRequired } from '../components/auth-required';
import { RatingForm } from '../components/rating-form';
import { StaffSection } from '../components/staff-card';
import { WaiverModal } from '../resources+/accept-waiver';

import { type Route } from './+types/_index';

export const handle = {
  title: 'Find Gyms & Classes',
};

export async function loader({ request, params }: Route.LoaderArgs) {
  const session = await getSessionIfAuthenticated(request);

  const { gymId } = await getGymIdAndSlugFromParams(params);

  const result = await getAthleteGymDetailsById(gymId, session?.userId);

  return result.mapErr(handleDomainError).unwrap();
}

const formatTime = format('h:mmaaa');

export default function Details() {
  const gym = useLoaderData<typeof loader>();
  const location = useLocation();
  const [showingWaiverModal, setShowingWaiverModal] = useState(false);

  const handleShowWaiverModal = () => {
    setShowingWaiverModal(true);
  };

  const handleCloseWaiverModal = () => {
    setShowingWaiverModal(false);
  };

  const stateAndZip = gym.stateAbbreviation && gym.zip ? `${gym.stateAbbreviation} ${gym.zip}` : '';

  const fullAddress = [gym.addressLine1, gym.addressLine2, gym.city, stateAndZip]
    .filter((x) => x)
    .join(', ');

  const formattedHours = gym.days.map((day) => {
    return {
      weekday: day.weekday,
      hours: day.open
        ? day.hours
            .map(
              (hour) =>
                `${formatTime(parseTime(hour.startTime))} - ${formatTime(parseTime(hour.endTime))}`,
            )
            .join(', ')
        : 'Closed',
    };
  });

  const returnRoute = `${location.pathname}${location.search}`;

  const hasHours = formattedHours.some((d) => d.hours !== 'Closed');

  const waiverAccepted = gym.waiver !== null && gym.waiver.acceptedAt !== null;

  return (
    <div>
      <OptionalDetailsSection title="Hours" hasDetails={hasHours}>
        <table>
          <tbody>
            {formattedHours.map((d) => (
              <tr key={d.weekday}>
                <th scope="row" className="w-24 text-left text-body2 font-bold">
                  {d.weekday}
                </th>
                <td className="text-body2">{d.hours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </OptionalDetailsSection>

      <hr className="my-4" />
      <OptionalDetailsSection title="Address">{fullAddress}</OptionalDetailsSection>

      <hr className="my-4" />
      <OptionalDetailsSection title="Description">{gym.description}</OptionalDetailsSection>

      <hr className="my-4" />
      <OptionalDetailsSection title="Our People" hasDetails={gym.staff.length > 0}>
        <StaffSection staff={gym.staff} />
      </OptionalDetailsSection>

      <hr className="my-4" />
      <OptionalDetailsSection title="Amenities" hasDetails={gym.amenities.length > 0}>
        <ul className="ml-6 list-disc">
          {gym.amenities.map((a) => (
            <li key={a} className="text-body2">
              {a}
            </li>
          ))}
        </ul>
      </OptionalDetailsSection>

      <hr className="my-4" />
      <OptionalDetailsSection
        title="Policies"
        hasDetails={gym.policies !== null || gym.waiver !== null}
      >
        <div className="flex flex-col items-start gap-2">
          {gym.policies !== null ? (
            <p className="whitespace-pre-wrap text-body2">{gym.policies}</p>
          ) : null}

          {gym.waiver !== null && !waiverAccepted ? (
            <Button onClick={handleShowWaiverModal}>Review & Sign Waiver</Button>
          ) : null}

          {gym.waiver !== null && waiverAccepted ? (
            <Button onClick={handleShowWaiverModal} variant="outline">
              <FileIcon className="mr-2" /> View Signed Waiver
            </Button>
          ) : null}
        </div>
      </OptionalDetailsSection>

      <hr className="my-4" />
      <OptionalDetailsSection
        title="Connect with Us"
        hasDetails={!!(gym.facebookUrl || gym.instagramUrl || gym.youtubeUrl)}
      >
        <div className="flex gap-2">
          {gym.facebookUrl ? (
            <a href={gym.facebookUrl} target="_blank" rel="noreferrer">
              <Icon name="facebook" size="xl" />
            </a>
          ) : null}

          {gym.instagramUrl ? (
            <a href={gym.instagramUrl} target="_blank" rel="noreferrer">
              <Icon name="instagram" size="xl" />
            </a>
          ) : null}

          {gym.youtubeUrl ? (
            <a href={gym.youtubeUrl} target="_blank" rel="noreferrer">
              <Icon name="video" size="xl" />
            </a>
          ) : null}
        </div>
      </OptionalDetailsSection>

      <hr className="my-4" />
      <h2 className="my-4 font-sans text-subtitle1">Rate this Gym</h2>
      <RatingForm
        gymSlug={gym.urlSlug ?? gym.id}
        rating={gym.userGymRating}
        returnRoute={returnRoute}
      />

      <Button
        asChild
        type="button"
        variant="outline"
        className="mt-4 border-secondary text-secondary"
      >
        <Link
          to={`/gym/${gym.urlSlug ?? gym.id}/report-a-problem?${new URLSearchParams([['returnRoute', returnRoute]])}`}
          preventScrollReset={true}
        >
          Report a Problem
        </Link>
      </Button>

      {showingWaiverModal && gym.waiver !== null ? (
        <AuthRequired>
          <WaiverModal
            open={true}
            onClose={handleCloseWaiverModal}
            gymName={gym.name}
            waiverId={gym.waiver.id}
            waiverContent={gym.waiver.html}
            acceptedAt={gym.waiver.acceptedAt}
            acceptedBy={gym.waiver.acceptedBy}
          />
        </AuthRequired>
      ) : null}
    </div>
  );
}
